import type { LotDoc, LotSearchStatus } from './lot';

/** A `lots` Typesense document as the projection writes it: flat, with every attribute beyond the identity optional. */
export interface LotTypesenseDoc {
  readonly id: string;
  readonly lotId: number;
  readonly organizationId: number;
  readonly productId: number;
  readonly warehouseId?: number;
  readonly lotNumber: string;
  readonly slug?: string;
  readonly description?: string;
  readonly status: LotSearchStatus;
  readonly commodity?: string;
  readonly commodityStyle?: string;
  readonly commodityLabel?: string;
  readonly commoditySize?: string;
  readonly commodityUnit?: string;
  readonly commodityGrade?: string;
  readonly grade?: string;
  readonly countryOfOrigin?: string;
  readonly organic?: boolean;
  readonly quantityAvailable?: number;
  readonly reserved?: number;
  readonly weight?: number;
  readonly receivedByWeight?: boolean;
  readonly lockReason?: string;
  readonly receiveDate?: string;
  readonly receiveDateTimestamp?: number;
  readonly repackDate?: string;
  readonly vendor?: string;
  readonly grower?: string;
  readonly palletTagIds?: readonly number[];
  readonly palletTagNumbers?: readonly string[];
}

/** The picker-facing `LotDoc` for a Typesense hit; `quantity` is the available quantity, as in the Algolia record. */
export const lotDocFromTypesense = (doc: LotTypesenseDoc): LotDoc => {
  const tagNumbers = doc.palletTagNumbers ?? [];
  const quantityAvailable = doc.quantityAvailable ?? 0;

  return {
    objectID: doc.id,
    lotId: doc.lotId,
    organizationId: doc.organizationId,
    productId: doc.productId,
    warehouseId: doc.warehouseId ?? 0,
    slug: doc.slug ?? doc.lotNumber,
    description: doc.description ?? '',
    status: doc.status,
    commodity: doc.commodity ?? '',
    commodityStyle: doc.commodityStyle ?? '',
    commodityLabel: doc.commodityLabel ?? '',
    commoditySize: doc.commoditySize ?? '',
    commodityUnit: doc.commodityUnit ?? '',
    ...(doc.commodityGrade === undefined ? {} : { commodityGrade: doc.commodityGrade }),
    countryOfOrigin: doc.countryOfOrigin ?? '',
    organic: doc.organic ?? false,
    quantity: quantityAvailable,
    quantityAvailable,
    ...(doc.lockReason === undefined ? {} : { lockReason: doc.lockReason }),
    receiveDateTimestamp: doc.receiveDateTimestamp ?? 0,
    receiveDate: doc.receiveDate ?? '',
    receivedByWeight: doc.receivedByWeight ?? false,
    weight: doc.weight ?? 0,
    reserved: doc.reserved ?? 0,
    vendor: doc.vendor ?? '',
    grower: doc.grower ?? '',
    palletTags: (doc.palletTagIds ?? []).map((palletTagId, index) => {
      const tagNumber = tagNumbers[index] ?? '';
      return { palletTagId, tagNumber, tagNumbers: [tagNumber] };
    }),
    grade: doc.grade ?? '',
    ...(doc.repackDate === undefined ? {} : { repackDate: doc.repackDate }),
    orders: [],
  };
};
